"use client";

import { Button } from "@universe/ui/components/button";
import { format } from "date-fns";
import { useTranslations } from "next-intl";
import React from "react";

import BlockchainNetworkIcon from "@/components/blockchain-network-icon";
import CollapsibleDetailSection from "@/components/collapsible-detail-section";
import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { BLOCKCHAIN_MAPPING } from "@/constants";
import { LEDGER_ENTRY_DATE_TIME_LONG_FORMAT } from "@/constants/dateTime";
import { useLedgerFieldLabel } from "@/utils/formatLedgerFieldLabel";
import { getBeneficiaryDisplayName } from "@/utils/getLegalEntityName";
import { getNestedProperty } from "@/utils/getNestedProperty";
import type { FilteredResolvedBlockchainTransaction } from "@generated/types";

import TransactionStatusBadge, {
  TransactionStatusBadgeType,
} from "../transaction-status-badge";

import LedgerEntryBaseFields from "./ledger-entry-base-fields";
import { MissingResolutionAlert } from "./missing-resolution-alert";

/**
 * At runtime, `blockchain` may be a string ID or an object with id/name/explorer.
 * The generated types only define it as `string`, so we use a runtime-aware type.
 */
interface BlockchainObject {
  id?: string;
  name?: string;
  explorer?: {
    transactionUrl?: string;
    addressUrl?: string;
  };
}

type BlockchainTransactionResolvedData = Omit<
  FilteredResolvedBlockchainTransaction,
  "blockchain"
> & {
  blockchain?: string | BlockchainObject;
};

interface BlockchainTransactionsDetailsProps {
  entry: LedgerEntryResolvedBlockchainTransactions;
}

/**
 * Get blockchain ID with fallback strategy:
 * 1. Try blockchain.id first (preferred) - for FilteredResolvedBlockchainTransaction
 * 2. Try blockchain as string (legacy) - for ResolvedJournalBlockchainTransactionData
 * 3. Fallback to networkFee.wallet.blockchain.id (legacy/older transactions)
 */
function getBlockchainId(resolvedData: BlockchainTransactionResolvedData) {
  const blockchain = resolvedData.blockchain;
  if (typeof blockchain === "object" && blockchain !== null) {
    return blockchain.id ?? resolvedData.networkFee?.wallet?.blockchain?.id;
  }
  return blockchain ?? resolvedData.networkFee?.wallet?.blockchain?.id;
}

/**
 * Get blockchain name with fallback strategy:
 * 1. Try blockchain.name first (preferred) - for FilteredResolvedBlockchainTransaction
 * 2. Fallback to networkFee.wallet.blockchain.name (legacy)
 * 3. Fallback to blockchain ID
 * 4. Return "Unknown" if nothing available
 */
function getBlockchainName(resolvedData: BlockchainTransactionResolvedData) {
  const blockchain = resolvedData.blockchain;
  if (typeof blockchain === "object" && blockchain !== null) {
    return (
      blockchain.name ??
      resolvedData.networkFee?.wallet?.blockchain?.name ??
      blockchain.id ??
      resolvedData.networkFee?.wallet?.blockchain?.id ??
      "Unknown"
    );
  }
  return (
    resolvedData.networkFee?.wallet?.blockchain?.name ??
    blockchain ??
    resolvedData.networkFee?.wallet?.blockchain?.id ??
    "Unknown"
  );
}

/**
 * Get explorer URLs with fallback strategy:
 * 1. Try blockchain.explorer first (preferred) - for FilteredResolvedBlockchainTransaction
 * 2. Fallback to networkFee.wallet.blockchain.explorer (legacy/older transactions)
 * 3. Return undefined if neither available
 */
function getExplorerUrls(resolvedData: BlockchainTransactionResolvedData) {
  const blockchain = resolvedData.blockchain;
  if (
    typeof blockchain === "object" &&
    blockchain !== null &&
    blockchain.explorer
  ) {
    return {
      transactionUrl: blockchain.explorer.transactionUrl,
      addressUrl: blockchain.explorer.addressUrl,
    };
  }

  if (resolvedData.networkFee?.wallet?.blockchain?.explorer) {
    return {
      transactionUrl:
        resolvedData.networkFee.wallet.blockchain.explorer.transactionUrl,
      addressUrl: resolvedData.networkFee.wallet.blockchain.explorer.addressUrl,
    };
  }

  return {
    transactionUrl: undefined,
    addressUrl: undefined,
  };
}

/**
 * Build explorer link by either replacing %s placeholder or concatenating
 */
function buildExplorerLink(
  urlTemplate: string | undefined,
  value: string | undefined
): string | null {
  if (!urlTemplate || !value) return null;

  if (urlTemplate.includes("%s")) {
    return urlTemplate.replace("%s", value);
  }

  return `${urlTemplate}${value}`;
}

type BlockchainReasonObject = {
  directionTranslation: string;
  displayName?: string;
  hideDisplayName?: boolean;
  amountTranslation: string;
  transactionTimeTranslation: string;
  transactionDirectionTranslation: string;
  forceNegative: boolean;
  forcePositive: boolean;
  walletAddress?: string;
  showReason?: boolean;
  reasonTranslation?: string;
};

const reasonObject: Record<string, BlockchainReasonObject> = {
  debit: {
    directionTranslation: "to",
    displayName: "resolvedRelatedPayment?.beneficiary?.name",
    amountTranslation: "amountSent",
    transactionTimeTranslation: "sentOn",
    transactionDirectionTranslation: "sentTo",
    forceNegative: true,
    forcePositive: false,
    walletAddress: "transfers?.[0]?.creditWallet?.address",
  },
  credit: {
    directionTranslation: "from",
    displayName: "debitBankAccount?.accountName",
    amountTranslation: "amountReceived",
    transactionTimeTranslation: "receivedOn",
    transactionDirectionTranslation: "receivedFrom",
    forceNegative: false,
    forcePositive: true,
    walletAddress: "transfers?.[0]?.debitWallet?.address",
  },
  "debit-rebate": {
    directionTranslation: "from",
    hideDisplayName: true,
    amountTranslation: "amountReceived",
    transactionTimeTranslation: "receivedOn",
    transactionDirectionTranslation: "receivedFrom",
    forceNegative: false,
    forcePositive: true,
    showReason: true,
    reasonTranslation: "debitRebate",
  },
  "credit-rebate": {
    directionTranslation: "from",
    hideDisplayName: true,
    amountTranslation: "amountReceived",
    transactionTimeTranslation: "receivedOn",
    transactionDirectionTranslation: "receivedFrom",
    forceNegative: false,
    forcePositive: true,
    showReason: true,
    reasonTranslation: "creditRebate",
  },
  "credit-deduction": {
    directionTranslation: "deduction",
    amountTranslation: "amountDeducted",
    transactionTimeTranslation: "deductedOn",
    transactionDirectionTranslation: "receivedFrom",
    forceNegative: true,
    forcePositive: false,
    walletAddress: "transfers?.[0]?.debitWallet?.address",
  },
  "debit-deduction": {
    directionTranslation: "deduction",
    amountTranslation: "amountDeducted",
    transactionTimeTranslation: "deductedOn",
    transactionDirectionTranslation: "sentTo",
    forceNegative: true,
    forcePositive: false,
    walletAddress: "transfers?.[0]?.creditWallet?.address",
  },
};

export default function BlockchainTransactionsDetails({
  entry,
}: BlockchainTransactionsDetailsProps) {
  const tLedgerFields = useTranslations("ledger.fields");
  const tCommon = useTranslations("common");
  const tError = useTranslations("error");
  const tWallet = useTranslations("wallet");

  // `fieldLabel` guards DYNAMIC, backend-driven `ledger.fields.*` keys (from the
  // `reason` object) so an empty/missing key never reaches `t()` → no next-intl
  // `onError` → no `MISSING_MESSAGE: ledger.fields.` Sentry event (CUSTOMER-UI-6M).
  // STATIC-literal calls (`tLedgerFields("status")`, `"network"`, …) stay direct.
  const fieldLabel = useLedgerFieldLabel();

  const reason = reasonObject[entry.reason as keyof typeof reasonObject];

  if (!reason) {
    return (
      <div className="flex items-center justify-center h-full text-center">
        <p className="text-gray-800">{tError("unableToDisplayInformation")}</p>
      </div>
    );
  }

  // BFF resolution failed (graceful degradation). Render base ledger fields
  // plus a support prompt — the dot-walks below assume a full resolved tx.
  if (!entry.resolvedData) {
    return (
      <div className="space-y-0">
        <LedgerEntryBaseFields entry={entry} />
        <MissingResolutionAlert />
      </div>
    );
  }

  const blockchainId = getBlockchainId(entry.resolvedData);
  const blockchainName = getBlockchainName(entry.resolvedData);
  const { transactionUrl, addressUrl } = getExplorerUrls(entry.resolvedData);
  const walletAddress =
    (getNestedProperty(
      entry.resolvedData as unknown as Record<string, unknown>,
      reason.walletAddress ?? ""
    ) as string) ?? entry.resolvedData.networkFee?.wallet?.address;

  const walletExplorerLink = buildExplorerLink(addressUrl, walletAddress);
  const txExplorerLink = buildExplorerLink(
    transactionUrl,
    entry.resolvedData.txHash
  );

  return (
    <div className="space-y-0">
      {/* eslint-disable @typescript-eslint/no-explicit-any */}
      {entry.reason === "debit" &&
        (entry.resolvedData as any).resolvedBlockchainWithdrawal
          ?.beneficiary && (
          <DetailRow
            label={tCommon("to")}
            value={getBeneficiaryDisplayName(
              (entry.resolvedData as any).resolvedBlockchainWithdrawal
                .beneficiary
            )}
            translateValue={false}
          />
        )}
      {/* eslint-enable @typescript-eslint/no-explicit-any */}
      <DetailRow
        label={fieldLabel(reason.amountTranslation)}
        value={
          <FormattedCurrencyAmount
            balance={entry.balance ?? 0}
            product={entry.product}
            forceNegative={reason.forceNegative}
            forcePositive={reason.forcePositive}
          />
        }
        translateValue={false}
      />
      <DetailRow
        label={tLedgerFields("status")}
        value={
          <TransactionStatusBadge
            size="xs"
            mobileSize="sm"
            status={
              (entry.resolvedData.transactionState ??
                "PROCESSING") as TransactionStatusBadgeType
            }
            showReconciled
          />
        }
      />

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={fieldLabel(reason.transactionTimeTranslation)}
        value={format(
          new Date(entry.resolvedData.transactionTime || ""),
          LEDGER_ENTRY_DATE_TIME_LONG_FORMAT
        )}
      />

      <hr className="my-2 opacity-65" />

      <CollapsibleDetailSection
        title={tWallet("header")}
        showLabel={tWallet("viewDetails")}
        hideLabel={tWallet("hideDetails")}
      >
        <div className="space-y-0">
          <DetailRow
            label={tLedgerFields("network")}
            value={
              <div className="flex items-center gap-2">
                <BlockchainNetworkIcon
                  size="24px"
                  network={blockchainId ?? ""}
                />
                <span>
                  {BLOCKCHAIN_MAPPING[
                    blockchainId as keyof typeof BLOCKCHAIN_MAPPING
                  ]?.name ?? blockchainName}
                </span>
              </div>
            }
            stack
            translateValue={false}
          />

          <DetailRow
            label={fieldLabel(reason.transactionDirectionTranslation)}
            textClassName="w-full bg-brand-primary/5 rounded-lg font-mono p-3 text-xs lg:text-sm"
            value={
              getNestedProperty(
                entry.resolvedData as unknown as Record<string, unknown>,
                reason.walletAddress ?? ""
              ) as string
            }
            stack
            copyable
            translateValue={false}
          />

          {walletExplorerLink && (
            <Button variant="outline" size="lg" className="w-full mb-2" asChild>
              <a
                href={walletExplorerLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tCommon("viewWalletOnExplorer")}
              </a>
            </Button>
          )}

          <DetailRow
            label={tLedgerFields("txHash")}
            textClassName="w-full bg-brand-primary/5 rounded-lg font-mono p-3 text-xs lg:text-sm"
            value={entry.resolvedData.txHash}
            stack
            copyable
            translateValue={false}
          />

          {txExplorerLink && (
            <Button variant="outline" size="lg" className="w-full mb-2" asChild>
              <a
                href={txExplorerLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tCommon("viewTransactionOnExplorer")}
              </a>
            </Button>
          )}
        </div>
      </CollapsibleDetailSection>

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("reason")}
        value={fieldLabel(reason.reasonTranslation)}
      />

      <DetailRow
        label={tLedgerFields("referenceId")}
        value={entry.resolvedData.reference}
        translateValue={false}
      />
    </div>
  );
}
